import { useState } from "react";
import type { ReactNode } from "react";

const STORAGE_KEY = "sixt-damage-access";

export function LoginGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) === "true",
  );

  if (unlocked) return <>{children}</>;

  return (
    <LoginScreen
      onUnlock={() => {
        sessionStorage.setItem(STORAGE_KEY, "true");
        setUnlocked(true);
      }}
    />
  );
}

function LoginScreen({ onUnlock }: { onUnlock: () => void }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!code || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (res.ok) {
        onUnlock();
      } else {
        setError("Invalid access code");
      }
    } catch {
      setError("Could not reach server");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sixt-black px-6 text-white">
      <form onSubmit={submit} className="w-full max-w-sm">
        <div className="text-[13px] font-semibold uppercase tracking-[0.2em] text-white">
          Damage Inspection
        </div>

        <h1 className="mt-10 text-3xl font-bold tracking-tight">
          Access code
        </h1>
        <p className="mt-2 text-sm text-white/60">
          Enter the code provided by your administrator to continue.
        </p>

        <div className="mt-8">
          <label
            htmlFor="access-code"
            className="text-[11px] font-semibold uppercase tracking-wider text-white/60"
          >
            Code
          </label>
          <input
            id="access-code"
            autoFocus
            type="password"
            autoComplete="off"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (error) setError(null);
            }}
            disabled={loading}
            placeholder="••••••••••"
            className="mt-2 w-full rounded-md border border-white/20 bg-white/5 px-3 py-3 text-base text-white placeholder:text-white/30 focus:border-accent focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-60"
          />
          {error && (
            <div className="mt-2 text-xs font-semibold text-[#FF7676]">
              {error}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={!code || loading}
          className="mt-6 w-full rounded-md bg-accent py-3 text-sm font-bold uppercase tracking-wide text-white transition-colors enabled:hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? "Checking…" : "Enter"}
        </button>

        <div className="mt-10 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
          Internal prototype · v0.1
        </div>
      </form>
    </div>
  );
}
